import { useTranslation } from "@/hooks";
import type { SupportedLanguage } from "@/services/locale";
import {
  useIsLoadingLanguages,
  useLanguages,
  useLoadLanguages,
} from "@/stores/language";
import type { Language } from "@/types/europafacts";
import { Flex, Image, Menu, Text } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { useEffect } from "react";
import "./LanguagePicker.css";

export function LanguagePicker() {
  const { currentLanguage, setLanguage } = useTranslation();
  const languages = useLanguages();
  const isLoadingLanguages = useIsLoadingLanguages();
  const loadLanguages = useLoadLanguages();

  useEffect(() => {
    loadLanguages();
  }, [loadLanguages]);

  const currentLanguageData =
    languages.find((lang: Language) => lang.iso2 === currentLanguage) || languages[0];

  const handleLanguageChange = (languageValue: string) => {
    setLanguage(languageValue as SupportedLanguage);
  };

  if (isLoadingLanguages || languages.length === 0) {
    return null;
  }

  return (
    <Menu width={200} shadow="lg" withArrow offset={10} position="bottom-end">
      <Menu.Target>
        <Flex align="center" gap={8} className="language-picker">
          <Image
            src={currentLanguageData?.flag_file || ""}
            alt={currentLanguageData?.language_name || ""}
            style={{ objectFit: "cover", width: 20, height: 14 }}
          />
          <Text fw={600} fz={13}>
            {currentLanguageData?.iso2?.toUpperCase() || ""}
          </Text>
          <IconChevronDown size={12} />
        </Flex>
      </Menu.Target>
      <Menu.Dropdown
        style={{
          maxHeight: "400px",
          overflowY: "auto",
        }}
      >
        {languages.map((language: Language) => {
          const isActive = language.iso2 === currentLanguage;
          return (
            <Menu.Item
              key={language.language_id}
              onClick={() => handleLanguageChange(language.iso2)}
              bg={isActive ? "#F0F2F6" : undefined}
            >
              <Flex align="center" gap="xs">
                <Image
                  src={language.flag_file || ""}
                  alt={language.language_name}
                  style={{ objectFit: "cover", width: 20, height: 14 }}
                />
                <Text fw={isActive ? 600 : 500} fz={15}>
                  {language.language_name}
                </Text>
              </Flex>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
